import React, { useState, useEffect } from "react";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import { useTranslation } from "react-i18next";
import Input from "../../components/common/input/Input";
import Textarea from "../../components/common/textarea/Textarea";
import Select from "../../components/common/select/Select";
import AuthService from "../../api/services/auth.service";
import MapLocationPicker from "../../components/common/map-location-picker/MapLocationPicker";
import LocationService from "../../api/services/locations.service";
import { toast } from "sonner";
import { setCookie } from "../../utils/cookies";
import styles from "./Settings.module.scss";

interface StoreInfo {
  id: number;
  email: string;
  name: string;
  description: string;
  logo_url: string;
  business_address: string;
  contact_phone: string;
  preferred_language: string;
  city_id?: number;
  country_id?: number;
  latitude?: number;
  longitude?: number;
  city?: {
    id: number;
    name: string;
  };
  country?: {
    id: number;
    name: string;
  };
}

const Settings = () => {
  const { t, i18n } = useTranslation("translation");
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form values
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    contact_phone: "",
    business_address: "",
    preferred_language: "",
    city_id: null as number | null,
    country_id: null as number | null,
    latitude: null as number | null,
    longitude: null as number | null,
  });

  // Countries and cities
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedCity, setSelectedCity] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Load store info
  useEffect(() => {
    const loadStoreInfo = async () => {
      setLoading(true);
      try {
        const response = await AuthService.getStoreInfo();
        const data = response.data;
        setStoreInfo(data);
        setFormValues({
          name: data.name || "",
          description: data.description || "",
          contact_phone: data.contact_phone || "",
          business_address: data.business_address || "",
          preferred_language: data.preferred_language || "",
          city_id: data.city_id || null,
          country_id: data.country_id || null,
          latitude: data.latitude || null,
          longitude: data.longitude || null,
        });
        if (data.country) {
          setSelectedCountry({ id: data.country.id, name: data.country.name });
        }
        if (data.city) {
          setSelectedCity({ id: data.city.id, name: data.city.name });
        }
      } catch (error) {
        console.error("Error loading store info:", error);
        toast.error(
          t("store_info_messages.error") || "Failed to load store information",
        );
      } finally {
        setLoading(false);
      }
    };
    loadStoreInfo();
  }, []);

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await LocationService.countries(1, 100);
        if (response.data?.data) {
          setCountries(
            response.data.data.map((c: any) => ({ id: c.id, name: c.name })),
          );
        }
      } catch (error) {
        console.error("Error loading countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (selectedCountry) {
      setLoadingCities(true);
      setCities([]);
      LocationService.cities(selectedCountry.id, 1, 100)
        .then((response) => {
          if (response.data?.data) {
            setCities(
              response.data.data.map((c: any) => ({ id: c.id, name: c.name })),
            );
            // If we have a city_id but no selected city, try to find it
            if (formValues.city_id && !selectedCity) {
              const city = response.data.data.find(
                (c: any) => c.id === formValues.city_id,
              );
              if (city) {
                setSelectedCity({ id: city.id, name: city.name });
              }
            }
          }
        })
        .catch((error) => {
          console.error("Error loading cities:", error);
        })
        .finally(() => {
          setLoadingCities(false);
        });
    } else {
      setCities([]);
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  const handleChange = (field: string, value: string | number | null) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (
    country: { id: string; name: string } | null,
  ) => {
    if (country) {
      const countryObj = { id: parseInt(country.id), name: country.name };
      setSelectedCountry(countryObj);
      handleChange("country_id", countryObj.id);
      handleChange("city_id", null);
      setSelectedCity(null);
    } else {
      setSelectedCountry(null);
      handleChange("country_id", null);
      handleChange("city_id", null);
      setSelectedCity(null);
    }
  };

  const handleCityChange = (city: { id: string; name: string } | null) => {
    if (city) {
      const cityObj = { id: parseInt(city.id), name: city.name };
      setSelectedCity(cityObj);
      handleChange("city_id", cityObj.id);
    } else {
      setSelectedCity(null);
      handleChange("city_id", null);
    }
  };

  const handleLocationChange = (lat: number, lng: number, address: string) => {
    handleChange("latitude", lat);
    handleChange("longitude", lng);
    handleChange("business_address", address);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("name", formValues.name);
      formData.append("description", formValues.description);
      formData.append("contact_phone", formValues.contact_phone);
      formData.append("business_address", formValues.business_address);
      formData.append("preferred_language", formValues.preferred_language);

      if (formValues.city_id) {
        formData.append("city_id", formValues.city_id.toString());
      }
      if (formValues.country_id) {
        formData.append("country_id", formValues.country_id.toString());
      }
      if (formValues.latitude) {
        formData.append("latitude", formValues.latitude.toString());
      }
      if (formValues.longitude) {
        formData.append("longitude", formValues.longitude.toString());
      }

      await AuthService.updateStoreInfo(formData);
      toast.success(
        t("store_info_messages.success") ||
          "Store information updated successfully",
      );

      // Update language if changed
      if (
        formValues.preferred_language &&
        formValues.preferred_language !== storeInfo?.preferred_language
      ) {
        setCookie("lux_store_lang", formValues.preferred_language, 365);
        await i18n.changeLanguage(formValues.preferred_language);
      }

      // Reload store info
      const response = await AuthService.getStoreInfo();
      const data = response.data;
      setStoreInfo(data);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("store_info_messages.error") ||
        "Failed to update store information";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <TitleUpdater title={t("settings.title")} />
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("settings.title")} />
      <div className={styles.content}>
        <h1 className={styles.title}>
          {t("settings.title") || "Store Settings"}
        </h1>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.form_section}>
            <h2 className={styles.section_title}>
              {t("settings.store_info") || "Store Information"}
            </h2>
            <Input
              label={t("labels.store_name") || "Store Name"}
              name="name"
              placeholder={t("placeholders.store_name") || ""}
              type="text"
              value={formValues.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={styles.input}
            />
            <Textarea
              label={t("labels.description") || "Description"}
              name="description"
              placeholder={t("placeholders.description") || ""}
              value={formValues.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className={styles.input}
            />
            <Select
              value={formValues.preferred_language || ""}
              options={[
                { id: "az", name: t("languages.az") },
                { id: "en", name: t("languages.en") },
                { id: "de", name: t("languages.de") },
                { id: "es", name: t("languages.es") },
              ]}
              label={t("labels.preferred_language") || "Preferred Language"}
              onChange={(value) => {
                if (value) {
                  handleChange("preferred_language", value.id);
                }
              }}
              className={styles.input}
            />
            <Input
              label={t("labels.phone") || "Phone"}
              name="contact_phone"
              placeholder={t("placeholders.phone") || ""}
              type="phone"
              value={formValues.contact_phone}
              onChange={(e) => handleChange("contact_phone", e.target.value)}
              className={styles.input}
              phone
            />
          </div>

          <div className={styles.form_section}>
            <h2 className={styles.section_title}>
              {t("settings.address") || "Store Address"}
            </h2>
            <Select
              value={selectedCountry ? selectedCountry.id.toString() : ""}
              options={countries.map((c) => ({
                id: c.id.toString(),
                name: c.name,
              }))}
              label={t("labels.country") || "Country"}
              onChange={handleCountryChange}
              className={styles.input}
              loading={loadingCountries}
            />
            <Select
              value={selectedCity ? selectedCity.id.toString() : ""}
              options={cities.map((c) => ({
                id: c.id.toString(),
                name: c.name,
              }))}
              label={t("labels.city") || "City"}
              onChange={handleCityChange}
              className={styles.input}
              disabled={!selectedCountry}
              loading={loadingCities}
            />
            <div className={styles.map_section}>
              <MapLocationPicker
                latitude={formValues.latitude}
                longitude={formValues.longitude}
                address={formValues.business_address}
                cityName={selectedCity?.name || null}
                countryName={selectedCountry?.name || null}
                onLocationChange={handleLocationChange}
                disabled={!selectedCity || !selectedCountry}
              />
            </div>
          </div>

          <div className={styles.form_actions}>
            <button
              type="submit"
              className={styles.submit_button}
              disabled={saving}
            >
              {saving
                ? t("buttons.loading") || "Saving..."
                : t("buttons.save") || "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
